"use client";

import { useId, useMemo, useState, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { setUserLocale } from "@/utils/locale";
import { Locale } from "@/i18n/config";
import { localeOptions } from "@/components/_localized/localizationData";
import LanguageIcon from "@/components/languageIcon/languageIcon";
import { RegularModal } from "@/components/modals/modals";
import pom from "@/utils/pom";
import { TFunction } from "@/types";

const supportedLocales = [
  {
    locale: "en-US",
    localizationString:
      localeOptions["en-US"]?.localizationString ?? "English (United States)",
    regionKey: "NA",
  },
  {
    locale: "en-AU",
    localizationString:
      localeOptions["en-AU"]?.localizationString ?? "Australia (English)",
    regionKey: "APAC",
  },
  {
    locale: "en-CA",
    localizationString:
      localeOptions["en-CA"]?.localizationString ?? "English (Canada)",
    regionKey: "NA",
  },
  {
    locale: "fr-CA",
    localizationString:
      localeOptions["fr-CA"]?.localizationString ?? "Français (Canada)",
    regionKey: "NA",
  },
];

type LanguageSelectorProps = {
  localizationEnabledFlag: boolean;
};

export const LanguageSelector = ({
  localizationEnabledFlag,
}: LanguageSelectorProps) => {
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("LanguageSelector");
  const contryListId = useId();
  const rawLocale = useLocale();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  const currentLocaleData = useMemo(() => {
    return (
      supportedLocales.find((al) => al.locale === rawLocale) ||
      supportedLocales[0]
    );
  }, [rawLocale]);

  // Filter options to ONLY include those in the current user's region
  const regionalOptions = useMemo(() => {
    return supportedLocales.filter(
      (l) => l.regionKey === currentLocaleData.regionKey
    );
  }, [currentLocaleData.regionKey]);

  const isToggleable = regionalOptions.length > 1 && localizationEnabledFlag;

  const handleLocaleChange = (newLocale: Locale) => {
    if (newLocale === currentLocaleData.locale) return;

    startTransition(async () => {
      await setUserLocale(newLocale);
      globalThis.location.reload();
    });
  };

  return (
    <>
      <LanguageButton
        isToggleable={isToggleable}
        setIsLanguageModalOpen={setIsLanguageModalOpen}
        currentLocaleLabel={currentLocaleData.localizationString}
        t={t}
      >
        <LanguageIcon
          locale={currentLocaleData.locale || "en-us"}
          width="20"
          height="20"
          aria-hidden
        />
        <span className="block whitespace-nowrap text-base">
          {currentLocaleData.localizationString}
        </span>
      </LanguageButton>

      {isToggleable && (
        <RegularModal
          isOpen={isLanguageModalOpen}
          onRequestClose={() => setIsLanguageModalOpen(false)}
          overlayClassName="fixed inset-0 bg-[#25333880] z-50 bg-opacity-50 flex justify-center items-end sm:items-center"
          className="w-full outline-none z-50 top-[initial] sm:top-0"
          contentClassName="bg-white sm:rounded-lg sm:max-w-md shadow-lg w-full h-[100%] sm:h-[auto] sm:min-h-[420px] mx-auto overflow-hidden pb-14 p-8 relative flex flex-col gap-8 rounded-t-lg"
          focusElementId={pom.accessibilityIds.chooseYourCountryTitle}
          contentLabel={t("dialogLabel")}
        >
          <h2
            aria-describedby={contryListId}
            id={pom.accessibilityIds.chooseYourCountryTitle}
            className="font-medium text-2xl sm:text-3xl text-[#373737]"
          >
            {t("chooseYourCountry")}
          </h2>

          <section id={contryListId} className="flex flex-col">
            <h3
              id={currentLocaleData.regionKey}
              className="text-base font-semibold text-black mb-5"
            >
              {t(`regions.${currentLocaleData.regionKey}`)}
            </h3>

            <ol
              className="list-none flex flex-col gap-5"
              aria-labelledby={currentLocaleData.regionKey}
            >
              {regionalOptions.map((locale) => (
                <li key={locale.locale}>
                  <button
                    className="flex items-center gap-3 group w-full text-left"
                    onClick={() => handleLocaleChange(locale.locale as Locale)}
                    disabled={isPending}
                    type="button"
                  >
                    <LanguageIcon
                      locale={locale.locale || "en-us"}
                      width="24"
                      height="24"
                      aria-hidden
                    />
                    <span className="text-base text-black font-normal group-hover:underline group-focus:underline">
                      {locale.localizationString}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        </RegularModal>
      )}
    </>
  );
};

type LanguageButtonProps = {
  children: React.ReactNode;
  isToggleable: boolean;
  setIsLanguageModalOpen: (value: boolean) => void;
  currentLocaleLabel: string;
  t: TFunction;
};

const LanguageButton = ({
  children,
  isToggleable,
  setIsLanguageModalOpen,
  currentLocaleLabel,
  t,
}: LanguageButtonProps) => {
  if (!isToggleable) {
    return (
      <span
        aria-label={currentLocaleLabel}
        className="flex items-center gap-2 text-base font-normal text-black cursor-default"
      >
        {children}
      </span>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <button
        className="flex items-center gap-2 text-base font-normal text-black hover:underline focus:underline cursor-pointer"
        aria-haspopup="dialog"
        aria-label={t("changeYourLanguage")}
        onClick={() => setIsLanguageModalOpen(true)}
        type="button"
      >
        {children}
      </button>
    </div>
  );
};
